<template>
  <div class="home detail-actions-page">
    <div class="px-8 py-4">
      <!-- Header -->
      <div class="detail-page-header mb-4">
        <button type="button" class="btn btn-link text-decoration-none detail-page-back" @click="$router.go(-1)" style="padding: 10px 0">
          <AppIcon name="bi-arrow-left" class="bi bi-arrow-left me-2" />{{$t('back')}}
        </button>
        <div class="detail-page-title-row d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
          <h3 class="detail-page-title mb-0">{{ invoice.reference }}</h3>
          <div class="detail-page-actions">
            <button v-if="helper.checkAccess('edit_supplier_invoices') != -1" @click="editRow()" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs detail-page-action--secondary">
              <AppIcon name="bi-pencil" class="bi bi-pencil" style="font-size:12px" />
              {{$t('edit')}}
            </button>
            <button v-if="helper.checkAccess('delete_supplier_invoices') != -1" @click="deleteRow()" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs detail-page-action--danger">
              <AppIcon name="bi-trash" class="bi bi-trash" style="font-size:12px" />
              {{$t('delete')}}
            </button>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center" style="padding:2rem">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Invoice Info -->
      <div v-else-if="invoice && invoice.id">
        <!-- Tabs -->
        <ul class="nav nav-tabs detail-page-tabs mb-4">
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'overview'}" @click="step = 'overview'">{{$t('overview')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_payments') != -1">
            <a class="nav-link" :class="{active: step === 'payments'}" @click="step = 'payments'">{{$t('payments')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_attachments') != -1">
            <a class="nav-link" :class="{active: step === 'attachments'}" @click="step = 'attachments'">{{$t('attachments')}}</a>
          </li>
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'history'}" @click="step = 'history'">{{$t('history')}}</a>
          </li>
        </ul>

        <!-- Tab Content -->
        <!-- Overview Tab -->
        <div v-if="step === 'overview'">
          <div class="row">
            <!-- Left Column: Invoice Details -->
            <div class="col-md-8">
              <div class="card mb-4">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-file-earmark-text" class="bi bi-file-earmark-text me-2" />{{$t('invoice')}} {{$t('information')}}</h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('reference')}}</small>
                      <strong>{{invoice.reference}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('status')}}</small>
                      <span class="badge" :class="{
                        'bg-success': invoice.status === 'paid',
                        'bg-warning': invoice.status === 'partially_paid',
                        'bg-danger': invoice.status === 'canceled',
                        'bg-secondary': invoice.status === 'pending'
                      }">
                        {{ $t(invoice.status) }}
                      </span>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('date')}}</small>
                      <strong>{{formatDate(invoice.date)}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('created_by')}}</small>
                      <strong>{{invoice.user ? invoice.user.name : '-'}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('created_at')}}</small>
                      <strong>{{formatDate(invoice.created_at)}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('updated_at')}}</small>
                      <strong>{{formatDate(invoice.updated_at)}}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Products Card -->
              <div class="card mb-4">
                <div class="card-header bg-white">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="mb-0"><AppIcon name="bi-box-seam" class="bi bi-box-seam me-2" />{{$t('products')}}</h6>
                    <div>
                        <button v-if="invoice.status != 'draft' && !reorderMode" @click="toggleReorder" style="padding: 2px 8px; font-size: 12px !important;" class="btn btn-outline-secondary btn-sm">
                            <AppIcon name="bi-arrow-down-up" class="bi bi-arrow-down-up me-1" style="font-size:12px" />
                            {{$t('reorder')}}
                        </button>
                        <div v-if="reorderMode" class="btn-group" role="group">
                            <button @click="saveOrder" style="padding: 2px 8px; font-size: 12px !important;" class="btn btn-success btn-sm">
                                <AppIcon name="bi-check" class="bi bi-check me-1" style="font-size:12px" /> {{$t('save')}}
                            </button>
                            <button @click="toggleReorder" style="padding: 2px 8px; font-size: 12px !important;" class="btn btn-danger btn-sm">
                                <AppIcon name="bi-x" class="bi bi-x me-1" style="font-size:12px" /> {{$t('cancel')}}
                            </button>
                        </div>
                    </div>
                  </div>
                </div>
                <div class="card-body table-responsive">
                  <table class="table mb-0 table-striped small">
                    <thead>
                      <tr class="text-secondary">
                        <th>{{$t('product_label')}}</th>
                        <th>{{$t('price')}}</th>
                        <th>{{$t('quantity')}}</th>
                        <th>{{$t('vat')}}</th>
                        <th>{{$t('sub_total')}}</th>
                        <th>{{$t('total')}}</th>
                        <th v-if="reorderMode" style="width: 100px;">{{$t('actions')}}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(product, index) in invoice.products" :key="product.id">
                        <td>{{product.product_label}}</td>
                        <td>{{formatMoney(product.price)}}</td>
                        <td>{{product.quantity}}</td>
                        <td>{{product.vat}} %</td>
                        <td>{{formatMoney(product.sub_total)}}</td>
                        <td>{{formatMoney(product.total)}}</td>
                        <td v-if="reorderMode">
                            <div class="btn-group">
                                <button type="button" class="btn btn-outline-secondary btn-sm" @click="moveUp(index)" :disabled="index === 0" style="padding: 2px 5px;"><AppIcon name="bi-arrow-up-short" class="bi bi-arrow-up-short" /></button>
                                <button type="button" class="btn btn-outline-secondary btn-sm" @click="moveDown(index)" :disabled="index === invoice.products.length - 1" style="padding: 2px 5px;"><AppIcon name="bi-arrow-down-short" class="bi bi-arrow-down-short" /></button>
                            </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Notes Card -->
              <div v-if="invoice.notes" class="card">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-sticky" class="bi bi-sticky me-2" />{{$t('notes')}}</h6>
                </div>
                <div class="card-body">
                  <p class="mb-0">{{ invoice.notes }}</p>
                </div>
              </div>
            </div>

            <!-- Right Column: Supplier & Balance Info -->
            <div class="col-md-4">
              <div class="card mb-4">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-building" class="bi bi-building me-2" />{{$t('supplier')}}</h6>
                </div>
                <div class="card-body">
                  <div v-if="!invoice.supplier" class="text-muted">{{$t('no_supplier_found')}}</div>
                  <div v-else>
                    <div class="mb-3">
                      <small class="text-muted d-block">{{$t('reference')}}</small>
                      <strong>{{invoice?.supplier?.reference}}</strong>
                    </div>
                    <div class="mb-3">
                      <small class="text-muted d-block">{{$t('name')}}</small>
                      <strong>{{invoice?.supplier?.name}}</strong>
                    </div>
                    <div class="mb-3">
                      <small class="text-muted d-block">{{$t('phone')}}</small>
                      <strong>{{invoice?.supplier?.phone || '-'}}</strong>
                    </div>
                    <div>
                      <small class="text-muted d-block">{{$t('email')}}</small>
                      <strong>{{invoice?.supplier?.email || '-'}}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Balance Card -->
              <div class="card">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-wallet2" class="bi bi-wallet2 me-2" />{{$t('balance')}}</h6>
                </div>
                <div class="card-body">
                  <div style="position: relative; height: 220px; margin-bottom: 20px;">
                    <canvas ref="invoiceBalanceChart" aria-label="Invoice Balance Chart" role="img"></canvas>
                  </div>
                  <div class="d-flex justify-content-between text-center">
                    <div>
                      <div class="text-dark d-block">{{$t('total')}}</div>
                      <span class="text-dark"><b>{{formatMoney(invoice.total)}}</b></span>
                    </div>
                    <div>
                      <div class="text-dark d-block">{{$t('amount_paid')}}</div>
                      <span class="text-dark"><b>{{formatMoney(amount_paid)}}</b></span>
                    </div>
                    <div>
                      <div class="text-dark d-block">{{$t('remaining')}}</div>
                      <span class="text-dark"><b>{{formatMoney(invoice.total - amount_paid)}}</b></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Other Tabs -->
        <div v-if="step === 'payments' && helper.checkAccess('view_payments') != -1">
          <payment-component v-if="invoice && invoice.id" :supplier_invoice_id="invoice.id" :isDetailSupplier="true"></payment-component>
        </div>

        <div v-if="step === 'attachments' && helper.checkAccess('view_attachments') != -1">
          <attachments :entity_id="invoice.id" entity_type="invoice" />
        </div>

        <div v-if="step === 'history'">
          <document-history
            :history="history"
            :loading="loadingHistory"
            :pagination="historyPagination"
            @pageChange="changeHistoryPage"
          />
        </div>
      </div>
    </div>


        <!-- Edit Modal -->
        <add-edit-invoice-modal 
            v-if="editModalisOpen" 
            @modalClosed="editModalisOpen = false" 
            modalId="edit-invoice-modal-01" 
            :isModalOpen="editModalisOpen"  
            :data="invoice" 
            action="update" 
        ></add-edit-invoice-modal>
        <!-- Edit modal -->

        <!-- quote modal -->
        <invoice-template-modal 
            v-if="invoiceModalisOpen" 
            @modalClosed="invoiceModalisOpen = false" 
            modalId="invoice-invoice-modal-01" 
            :isModalOpen="invoiceModalisOpen"  
            :data="invoice" 
            type="invoice">
        </invoice-template-modal>
        <!-- quote modal -->
        

    </div>

</template>

<script>
import helper from '../../helper'
import moment from 'moment'
import Chart from 'chart.js'

import AddEditInvoiceModal from '../../components/supplierInvoice/AddEditInvoiceModal';
import InvoiceTemplateModal from '../../components/invoiceLayout/InvoiceTemplateModal';
import PaymentComponent from '../../components/PaymentComponent.vue';
import DocumentHistory from '../../components/DocumentHistory.vue';


import attachments from '../../components/Attachment.vue'

export default {
  name: 'InvoiceDetails',
  components :{
    attachments,
    AddEditInvoiceModal,
    InvoiceTemplateModal,
    PaymentComponent,
    DocumentHistory
  },
  data(){
    return {
        loading : false,
        loadingHistory : false,
        invoice : {},
        history : [],
        historyPagination : {
            count: 0,
            per_page: 10,
            current_page: 1,
            total_pages: 1
        },
        amount_paid : 0,
        total_purchases : 0,
        total_quote : 0,
        invoiceModalisOpen : false,
        step : 'overview',
        invoiceModal : null,
        editModal : null,
        editModalisOpen : false,
        createClientState : false,
        createCreditInvoiceModalState :false,
        invoiceBalanceChart: null,
        reorderMode: false,
        originalProductsOrder: null
    }
  },
  created(){
    if(!this.$route.params || !this.$route.params.id) return this.$route.push('/supplier-invoices')
    this.getInvoice(this.$route.params.id)
  },
  mounted(){
    window.mixpanel.track("supplier_invoice_details");

    //check permissions
    if(helper.checkAccess('view_supplier_invoices') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_supplier_invoices') == -2) return this.$router.push({path : '/subscribe'})

  },
  beforeDestroy(){
    if(this.invoiceBalanceChart){
      this.invoiceBalanceChart.destroy()
      this.invoiceBalanceChart = null
    }
  },
  methods : {
        async getInvoice(id){
        try{
            this.loading = true
            const response = await helper.customGet(`${process.env.VUE_APP_API}/supplier-invoices/${id}`)
            this.invoice = response.data
            this.total = this.invoice.total
            this.amount_paid = this.invoice.transactions ? this.invoice.transactions.filter(t => t.status === 'paid').map(t => t.amount).reduce((a, c) => a + c, 0) : 0
            this.loading = false
            this.$nextTick(() => this.buildInvoiceBalanceChart())
        }catch(error){
            this.$router.push({path : '/supplier-invoices'})
            this.loading = false
        }
        },
        buildInvoiceBalanceChart(){
            const ctx = this.$refs.invoiceBalanceChart
            if(!ctx){ return }
            if(this.invoiceBalanceChart){
              this.invoiceBalanceChart.destroy()
              this.invoiceBalanceChart = null
            }
            const total = Number(this.invoice && this.invoice.total) || Number(this.total) || 0
            const paid = Number(this.amount_paid) || 0
            const remaining = Math.max(total - paid, 0)
            const hasData = (paid + remaining) > 0
            const data = hasData ? [paid, remaining] : [1, 0]
            const labels = [this.$t('amount_paid'), this.$t('remaining')]
            this.invoiceBalanceChart = new Chart(ctx, {
                type: 'pie',
                data: {
                    labels,
                    datasets: [{
                        data,
                        backgroundColor: ['#34c38f', '#ff6b6b'],
                        borderColor: ['#ffffff', '#ffffff'],
                        borderWidth: 2
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    legend: { position: 'bottom' },
                    tooltips: {
                        callbacks: {
                            label: (tooltipItem, chartData) => {
                                const idx = tooltipItem.index
                                const label = chartData.labels[idx]
                                const val = chartData.datasets[0].data[idx]
                                return `${label}: ${this.formatMoney ? this.formatMoney(val) : val}`
                            }
                        }
                    }
                }
            })
        },
        async getSupplierInvoiceHistory(id){
            try{
                this.loadingHistory = true
                const response = await helper.customGet(`${process.env.VUE_APP_API}/supplier-invoices/${id}/history`)
                this.history = response.data.data || response.data
                this.historyPagination = {
                    count: response.data.count || 0,
                    per_page: response.data.per_page || 10,
                    current_page: response.data.current_page || 1,
                    total_pages: response.data.total_pages || 1
                }
                this.loadingHistory = false
            }catch(error){
                alert('Error fetching history:', error)
                this.loadingHistory = false
            }
        },
        async changeHistoryPage(page){
            if(page < 1 || page > this.historyPagination.total_pages) return;
            
            try{
                this.loadingHistory = true
                const response = await helper.customGet(`${process.env.VUE_APP_API}/supplier-invoices/${this.invoice.id}/history?page=${page}&per_page=${this.historyPagination.per_page}`)
                this.history = response.data.data || response.data
                this.historyPagination = {
                    count: response.data.count || 0,
                    per_page: response.data.per_page || 10,
                    current_page: response.data.current_page || 1,
                    total_pages: response.data.total_pages || 1
                }
                this.loadingHistory = false
            }catch(error){
                alert('Error fetching history page:', error)
                this.loadingHistory = false
            }
        },
        editRow(){
            // this.clearForm()
            this.editModalisOpen = true
            this.invoice.date = moment(this.invoice.date).format('YYYY-MM-DD')
            // this.quoteProducts = this.quote.products
        },
        clearForm(){
            this.invoice = {
                id : null,
                reference : '',
                client_id : null,
                client : {},
                date : '',
                total : 0,
                products : []
            }
        },
        async deleteRow(){
            this.$swal.fire({
            title: `${this.$t('are_you_sure')}?`,
            text : this.$t('you_cant_revert'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: this.$t('yes_delete_it')
            }).then(async (result) => {
                if (result.isConfirmed) {
                    await helper.customDelete(`${process.env.VUE_APP_API}/supplier-invoices/${this.invoice.id}`)
                    this.$router.push({path : '/supplier-invoices'})
                }
            })
        },
        async transferCreditNote(){

            this.invoice.total = this.invoice.products && this.invoice.products.length ? this.invoice.products.map(o => o.total).reduce((a, c) => { return a + c }) : 0;

            this.createCreditInvoiceModalState = !this.createCreditInvoiceModalState

        },
        createCreditInvoiceModal(data){
            this.createCreditInvoiceModalState = data
        },
        formatDate(date) { return helper.formatDate(date) },
        formatMoney(number) { return helper.formatMoney(number);},
        formatReference(number,id) { return helper.formatReference(number,id);},
        toggleReorder() {
            if (!this.reorderMode) {
                // Enter reorder mode: save current order to restore if cancelled
                this.originalProductsOrder = JSON.parse(JSON.stringify(this.invoice.products));
                this.reorderMode = true;
            } else {
                // Cancel reorder mode: restore original order
                this.invoice.products = this.originalProductsOrder;
                this.reorderMode = false;
                this.originalProductsOrder = null;
            }
        },
        moveUp(index) {
            if (index > 0) {
                const products = [...this.invoice.products];
                const temp = products[index];
                products[index] = products[index - 1];
                products[index - 1] = temp;
                this.invoice.products = products;
            }
        },
        moveDown(index) {
            if (index < this.invoice.products.length - 1) {
                const products = [...this.invoice.products];
                const temp = products[index];
                products[index] = products[index + 1];
                products[index + 1] = temp;
                this.invoice.products = products;
            }
        },
        async saveOrder() {
            try {
                this.loading = true;
                
                const products = this.invoice.products.map((p, index) => {
                    return {
                        inventory_id: p.inventory_id,
                        product_id: p.product_id,
                        product_label: p.product_label,
                        quantity: p.quantity,
                        price: p.price,
                        unity: p.unity,
                        purchase_price: p.purchase_price,
                        vat: p.vat,
                        total: p.total,
                        sub_total: p.sub_total,
                        custom_product: !!p.custom_product,
                        discount: p.discount,
                        position: index
                    }
                })

                await helper.customPatch(`${process.env.VUE_APP_API}/supplier-invoices/${this.invoice.id}`, {
                    reference: this.invoice.reference,
                    date: this.invoice.date,
                    supplier_id: this.invoice.supplier_id,
                    purchase_id: this.invoice.purchase_id,
                    notes: this.invoice.notes,
                    status: this.invoice.status,
                    products: products
                });

                this.loading = false;
                this.reorderMode = false;
                this.originalProductsOrder = null;
                
                this.$toast.open({
                    message: this.$t('updated_successfully'),
                    type: 'success',
                    position: 'bottom'
                });
                
                // Refresh data
                this.getInvoice(this.$route.params.id);
                
            } catch (error) {
                this.loading = false;
                this.$toast.open({
                    message: error.response?.data?.message || this.$t('error_occurred'),
                    type: 'error',
                    position: 'bottom'
                });
            }
        }

    },
    computed : {
        business(){ return this.$store.getters.BUSINESS},
        helper(){ return helper},
        total(){ return this.invoice.products.length ? this.invoice.products.map(o => o.total).reduce((a, c) => { return a + c }) : 0},
        sub_total(){ return this.invoice.products.length ? this.invoice.products.map(o => o.sub_total).reduce((a, c) => { return a + c }) : 0}
    },
    watch : {
        editModalisOpen(newValue) {
            if(newValue === false){
                this.getInvoice(this.$route.params.id)
            }
        },
        step(newValue) {
            if(newValue === 'history' && this.invoice.id){
                this.getSupplierInvoiceHistory(this.invoice.id)
            }
            if(newValue === 'overview'){
                this.$nextTick(() => this.buildInvoiceBalanceChart())
            }
        }
    }
}
</script>

<style scoped>
    .table-borderless>:not(caption)>*>*{
        padding: 5px !important
    }
</style>
